import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import { apiFetch } from '../lib/api';
import { usePermissions } from '../hooks/usePermissions';
import { PERMISSIONS } from '../lib/permissions';
import {
  Store, Search, X, CheckCircle, Loader2, Plus, DollarSign,
  Download, Eye, Edit2, Power, PowerOff, MapPin, ShoppingBag,
  Save, Trash2, BarChart3, Truck, Copy, ExternalLink
} from 'lucide-react';
import * as XLSX from 'xlsx';

interface Merchant {
  code: string;
  name: string;
  ownerPhone: string;
  ownerUserId?: number;
  commissionRate: number;
  isActive: number | boolean;
  supportsPickup: number | boolean;
  pickupLocation?: string;
  pickupTimeNote?: string;
  deliveryDays?: number[];
  deliveryTime?: string;
  deliveryCutoffTime?: string;
  deliveryCutoffDaysBefore?: number;
  isClosed?: number | boolean;
  closedUntil?: string;
  closedMessage?: string;
  monthRevenue?: number;
  monthPendingCommission?: number;
  monthPaidCommission?: number;
  createdAt?: number;
  updatedAt?: number;
}

interface MerchantSummary {
  totalMonthRevenue: number;
  totalMonthPendingCommission: number;
  totalMonthPaidCommission: number;
}

interface MerchantEstate {
  id: number;
  groupCode: string;
  name: string;
  region: string;
  area: string;
  minOrderAmount: number;
  isActive: number | boolean;
  sortOrder: number;
}

interface MerchantEstateSettingRow {
  id: number;
  name: string;
  region: string;
  district?: string;
  area?: string;
  estateType?: string;
  settingId?: number;
  minOrderAmount: number;
  isActive: boolean;
}

interface MerchantOrder {
  id: number;
  orderNum: string;
  name: string;
  phone: string;
  address: string;
  totalPrice: number;
  gsCode?: string;
  createdAt: number;
}

interface MerchantCommission {
  id: number;
  merchantCode: string;
  orderId: number;
  orderTotal: number;
  commissionRate: number;
  amount: number;
  status: 'pending' | 'paid';
  month: string;
  paidAt?: number;
  createdAt: number;
}

interface MerchantDetail {
  merchant: Merchant;
  dashboardUrl?: string | null;
  estates: MerchantEstate[];
  recentOrders: MerchantOrder[];
  commissions: MerchantCommission[];
}

interface ReportData {
  month: string;
  merchant: Merchant;
  orders: MerchantOrder[];
  commissions: MerchantCommission[];
  summary: {
    revenue: number;
    pendingCommission: number;
    paidCommission: number;
  };
}

interface ReferralReportRow {
  merchantCode: string;
  merchantName: string;
  clicks: number;
  orders: number;
}

interface ReferralReport {
  clicks: number;
  conversions: number;
  byMerchant: ReferralReportRow[];
}

const STATUS_BADGES: Record<string, string> = {
  active: 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-300',
  inactive: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
  pending: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-300',
  paid: 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-300',
};

const REGIONS = [
  { value: 'NT', label: '新界' },
  { value: 'KL', label: '九龍' },
  { value: 'HK', label: '香港島' },
];

function formatCents(cents = 0): string {
  return `HK$${Math.round(cents / 100).toLocaleString('zh-HK')}`;
}

function formatDate(ts?: number): string {
  if (!ts) return '-';
  return new Date(ts * 1000).toLocaleDateString('zh-HK');
}

function formatDeliveryDays(days?: number[]): string {
  const labels = ['日', '一', '二', '三', '四', '五', '六'];
  if (!days || days.length === 0) return '未設定';
  return days.map((d) => `星期${labels[d]}`).join('、');
}

function validateTime(value: string): string | null {
  if (!value) return null;
  if (!/^([0-1]?\d|2[0-3]):([0-5]\d)$/.test(value)) {
    return '截單時間格式須為 HH:MM（00:00–23:59）';
  }
  return null;
}

function maskPhone(phone: string): string {
  const s = String(phone || '').replace(/\D/g, '');
  if (s.length <= 4) return s;
  return `${s.slice(0, 2)}****${s.slice(-2)}`;
}

function toBool(v: number | boolean | undefined): boolean {
  return v === 1 || v === true;
}

function getCurrentMonth(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

const AdminMerchants: React.FC = () => {
  const navigate = useNavigate();
  const { can } = usePermissions();

  const [merchants, setMerchants] = useState<Merchant[]>([]);
  const [summary, setSummary] = useState<MerchantSummary>({
    totalMonthRevenue: 0,
    totalMonthPendingCommission: 0,
    totalMonthPaidCommission: 0,
  });
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');

  const [detailCode, setDetailCode] = useState<string | null>(null);
  const [detail, setDetail] = useState<MerchantDetail | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [copiedDashboardUrl, setCopiedDashboardUrl] = useState(false);

  const [estateSettings, setEstateSettings] = useState<MerchantEstateSettingRow[]>([]);
  const [estateSettingsLoading, setEstateSettingsLoading] = useState(false);
  const [estateSettingsError, setEstateSettingsError] = useState<string | null>(null);
  const [estateDrafts, setEstateDrafts] = useState<Record<number, { minOrderAmount: number; isActive: boolean }>>({});

  const [estateRegionFilter, setEstateRegionFilter] = useState<'all' | 'HK' | 'KL' | 'NT'>('all');
  const [estateDistrictFilter, setEstateDistrictFilter] = useState<string>('all');
  const [estateAreaFilter, setEstateAreaFilter] = useState<string>('all');
  const [estateTypeFilter, setEstateTypeFilter] = useState<string>('all');
  const [estateSearch, setEstateSearch] = useState('');
  const [estatePage, setEstatePage] = useState(1);
  const ESTATES_PER_PAGE = 10;

  // 切換篩選時自動回到第一頁
  useEffect(() => {
    setEstatePage(1);
  }, [estateRegionFilter, estateDistrictFilter, estateAreaFilter, estateTypeFilter, estateSearch]);

  const [referralReportMonth, setReferralReportMonth] = useState<string>(getCurrentMonth());
  const [referralReport, setReferralReport] = useState<ReferralReport | null>(null);
  const [referralReportLoading, setReferralReportLoading] = useState(false);
  const [referralReportError, setReferralReportError] = useState<string | null>(null);

  const [editingMerchant, setEditingMerchant] = useState<Merchant | null>(null);
  const [editForm, setEditForm] = useState<Partial<Merchant>>({});

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState({
    code: '',
    name: '',
    ownerPhone: '',
    commissionRate: 15,
    supportsPickup: false,
    pickupLocation: '',
    pickupTimeNote: '',
    deliveryDays: [2, 4, 6] as number[],
    deliveryTime: '16:00-18:00',
    deliveryCutoffTime: '20:00',
    deliveryCutoffDaysBefore: 1,
    isClosed: false,
    closedUntil: '',
    closedMessage: '',
  });

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin');
      return;
    }
    loadMerchants();
  }, [navigate]);

  useEffect(() => {
    if (detailCode) {
      loadReferralReport(referralReportMonth);
    }
  }, [detailCode, referralReportMonth]);

  const loadMerchants = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch('/api/public/admin/merchants');
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || '獲取商家列表失敗');
      }
      const json = await res.json();
      setMerchants(Array.isArray(json.data) ? json.data : []);
      setSummary(json.summary || {
        totalMonthRevenue: 0,
        totalMonthPendingCommission: 0,
        totalMonthPaidCommission: 0,
      });
    } catch (e: any) {
      setError(e.message || '加載失敗');
    } finally {
      setLoading(false);
    }
  };

  const filteredMerchants = useMemo(() => {
    return merchants.filter((m) => {
      const matchesSearch =
        m.name.toLowerCase().includes(search.toLowerCase()) ||
        m.code.toLowerCase().includes(search.toLowerCase()) ||
        m.ownerPhone.includes(search);
      const matchesStatus =
        statusFilter === 'all' ? true :
        statusFilter === 'active' ? toBool(m.isActive) : !toBool(m.isActive);
      return matchesSearch && matchesStatus;
    });
  }, [merchants, search, statusFilter]);

  const handleToggleActive = async (merchant: Merchant) => {
    if (!can(PERMISSIONS.MERCHANTS_MANAGE)) {
      setError('沒有權限');
      return;
    }
    const nextActive = !toBool(merchant.isActive);
    if (!confirm(`確認${nextActive ? '啟用' : '停用'}商家「${merchant.name}」？`)) return;
    setActionLoading(true);
    try {
      const res = await apiFetch(`/api/public/admin/merchants/${merchant.code}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ isActive: nextActive }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setSuccess(`商家已${nextActive ? '啟用' : '停用'}`);
        loadMerchants();
      } else {
        setError(data.error || '操作失敗');
      }
    } catch (e: any) {
      setError(e.message || '操作失敗');
    } finally {
      setActionLoading(false);
      setTimeout(() => { setSuccess(null); setError(null); }, 3000);
    }
  };

  const handleMarkCommissionPaid = async (commission: MerchantCommission) => {
    if (!can(PERMISSIONS.MERCHANTS_MANAGE)) {
      setError('沒有權限');
      return;
    }
    if (!confirm(`確認標記佣金 HK$${Math.round(commission.amount / 100)} 為已支付？`)) return;
    setActionLoading(true);
    try {
      const res = await apiFetch(
        `/api/public/admin/merchants/${commission.merchantCode}/commissions/${commission.id}/pay`,
        { method: 'POST' }
      );
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setSuccess('佣金已標記為已支付');
        loadMerchants();
        if (detailCode) loadDetail(detailCode);
      } else {
        setError(data.error || '標記失敗');
      }
    } catch (e: any) {
      setError(e.message || '標記失敗');
    } finally {
      setActionLoading(false);
      setTimeout(() => { setSuccess(null); setError(null); }, 3000);
    }
  };

  const loadDetail = async (code: string) => {
    setLoadingDetail(true);
    try {
      const res = await apiFetch(`/api/public/admin/merchants/${code}`);
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || '獲取商家詳情失敗');
      }
      const json = await res.json();
      setDetail(json as MerchantDetail);
    } catch (e: any) {
      setError(e.message || '獲取詳情失敗');
    } finally {
      setLoadingDetail(false);
    }
  };

  const openDetail = async (code: string) => {
    setDetailCode(code);
    setDetail(null);
    setEstateSettings([]);
    setEstateSettingsError(null);
    setEstateDrafts({});
    await loadDetail(code);
    await loadEstateSettings(code);
  };

  const updateEstateDraft = (id: number, patch: Partial<{ minOrderAmount: number; isActive: boolean }>) => {
    setEstateDrafts(prev => ({
      ...prev,
      [id]: { minOrderAmount: prev[id]?.minOrderAmount ?? 0, isActive: prev[id]?.isActive ?? false, ...patch },
    }));
  };

  const handleJoinEstate = async (row: MerchantEstateSettingRow) => {
    if (!detailCode) return;
    const draft = estateDrafts[row.id] || { minOrderAmount: 0, isActive: false };
    setActionLoading(true);
    try {
      const res = await apiFetch(`/api/public/admin/merchants/${encodeURIComponent(detailCode)}/estate-settings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ estateId: row.id, minOrderAmount: draft.minOrderAmount, isActive: draft.isActive }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || '加入失敗');
      setSuccess('已加入服務');
      loadEstateSettings(detailCode);
    } catch (e: any) {
      setError(e.message || '加入失敗');
    } finally {
      setActionLoading(false);
      setTimeout(() => { setSuccess(null); setError(null); }, 3000);
    }
  };

  const handleSaveEstateSetting = async (row: MerchantEstateSettingRow) => {
    if (!detailCode || !row.settingId) return;
    const draft = estateDrafts[row.id] || { minOrderAmount: row.minOrderAmount || 0, isActive: row.isActive };
    setActionLoading(true);
    try {
      const res = await apiFetch(`/api/public/admin/merchants/${encodeURIComponent(detailCode)}/estate-settings/${row.settingId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ minOrderAmount: draft.minOrderAmount, isActive: draft.isActive }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || '儲存失敗');
      setSuccess('設定已儲存');
      loadEstateSettings(detailCode);
    } catch (e: any) {
      setError(e.message || '儲存失敗');
    } finally {
      setActionLoading(false);
      setTimeout(() => { setSuccess(null); setError(null); }, 3000);
    }
  };

  const handleRemoveEstateSetting = async (row: MerchantEstateSettingRow) => {
    if (!detailCode || !row.settingId) return;
    if (!confirm(`確定要移除此商家對「${row.name}」的服務設定嗎？`)) return;
    setActionLoading(true);
    try {
      const res = await apiFetch(`/api/public/admin/merchants/${encodeURIComponent(detailCode)}/estate-settings/${row.settingId}`, {
        method: 'DELETE',
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || '移除失敗');
      setSuccess('已移除服務');
      loadEstateSettings(detailCode);
    } catch (e: any) {
      setError(e.message || '移除失敗');
    } finally {
      setActionLoading(false);
      setTimeout(() => { setSuccess(null); setError(null); }, 3000);
    }
  };

  const loadReferralReport = async (month: string) => {
    setReferralReportLoading(true);
    setReferralReportError(null);
    setReferralReport(null);
    try {
      const res = await apiFetch(`/api/public/admin/merchant-referral-report?month=${encodeURIComponent(month)}`);
      if (res.status === 404) {
        setReferralReportError('報表正在準備中，請稍後再試。');
        return;
      }
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || '獲取推薦流量報表失敗');
      }
      const data = await res.json();
      setReferralReport({
        clicks: Number(data.clicks || 0),
        conversions: Number(data.conversions || 0),
        byMerchant: Array.isArray(data.byMerchant) ? data.byMerchant : [],
      });
    } catch (e: any) {
      setReferralReportError(e.message || '獲取推薦流量報表失敗');
    } finally {
      setReferralReportLoading(false);
    }
  };

  const loadEstateSettings = async (code: string) => {
    setEstateSettingsLoading(true);
    setEstateSettingsError(null);
    try {
      const res = await apiFetch(`/api/public/admin/merchants/${encodeURIComponent(code)}/estate-settings`);
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || '獲取商家屋苑設定失敗');
      }
      const data = await res.json();
      const rows = Array.isArray(data) ? data : [];
      setEstateSettings(rows);
      const drafts: Record<number, { minOrderAmount: number; isActive: boolean }> = {};
      rows.forEach((row: MerchantEstateSettingRow) => {
        drafts[row.id] = { minOrderAmount: row.minOrderAmount || 0, isActive: row.isActive };
      });
      setEstateDrafts(drafts);
    } catch (e: any) {
      setEstateSettingsError(e.message || '獲取商家屋苑設定失敗');
    } finally {
      setEstateSettingsLoading(false);
    }
  };

  const filteredEstateSettings = useMemo(() => {
    const term = estateSearch.trim().toLowerCase();
    return estateSettings
      .filter((row) => {
        const matchesRegion = estateRegionFilter === 'all' || row.region === estateRegionFilter;
        const matchesDistrict = estateDistrictFilter === 'all' || row.district === estateDistrictFilter;
        const matchesArea = estateAreaFilter === 'all' || row.area === estateAreaFilter;
        const matchesType = estateTypeFilter === 'all' || row.estateType === estateTypeFilter;
        const matchesSearch = !term ||
          row.name.toLowerCase().includes(term) ||
          (row.district && row.district.toLowerCase().includes(term)) ||
          (row.area && row.area.toLowerCase().includes(term)) ||
          (row.estateType && row.estateType.toLowerCase().includes(term));
        return matchesRegion && matchesDistrict && matchesArea && matchesType && matchesSearch;
      })
      .sort((a, b) => {
        if (a.isActive !== b.isActive) return a.isActive ? -1 : 1;
        return a.name.localeCompare(b.name, 'zh-HK');
      });
  }, [estateSettings, estateRegionFilter, estateDistrictFilter, estateAreaFilter, estateTypeFilter, estateSearch]);

  const estateTotalPages = Math.max(1, Math.ceil(filteredEstateSettings.length / ESTATES_PER_PAGE));
  const paginatedEstateSettings = useMemo(() => {
    const start = (estatePage - 1) * ESTATES_PER_PAGE;
    return filteredEstateSettings.slice(start, start + ESTATES_PER_PAGE);
  }, [filteredEstateSettings, estatePage]);

  const districtOptions = useMemo(() => {
    const source = estateRegionFilter === 'all' ? estateSettings : estateSettings.filter((r) => r.region === estateRegionFilter);
    const districts = new Set<string>();
    source.forEach((row) => {
      if (row.district) districts.add(row.district);
    });
    return ['全部', ...Array.from(districts).sort()];
  }, [estateSettings, estateRegionFilter]);

  const areaOptions = useMemo(() => {
    let source = estateSettings;
    if (estateRegionFilter !== 'all') source = source.filter((r) => r.region === estateRegionFilter);
    if (estateDistrictFilter !== 'all') source = source.filter((r) => r.district === estateDistrictFilter);
    const areas = new Set<string>();
    source.forEach((row) => {
      if (row.area) areas.add(row.area);
    });
    return ['全部', ...Array.from(areas).sort()];
  }, [estateSettings, estateRegionFilter, estateDistrictFilter]);

  const estateTypeOptions = useMemo(() => {
    const types = new Set<string>();
    estateSettings.forEach((row) => {
      if (row.estateType) types.add(row.estateType);
    });
    return ['全部', ...Array.from(types).sort()];
  }, [estateSettings]);

  const openEdit = (merchant: Merchant) => {
    setEditingMerchant(merchant);
    setEditForm({
      name: merchant.name,
      commissionRate: merchant.commissionRate,
      isActive: toBool(merchant.isActive),
      supportsPickup: toBool(merchant.supportsPickup),
      pickupLocation: merchant.pickupLocation || '',
      pickupTimeNote: merchant.pickupTimeNote || '',
      deliveryDays: merchant.deliveryDays || [],
      deliveryTime: merchant.deliveryTime || '16:00-18:00',
      deliveryCutoffTime: merchant.deliveryCutoffTime || '20:00',
      deliveryCutoffDaysBefore: merchant.deliveryCutoffDaysBefore || 1,
      isClosed: toBool(merchant.isClosed),
      closedUntil: merchant.closedUntil || '',
      closedMessage: merchant.closedMessage || '',
    });
  };

  const closeEdit = () => {
    setEditingMerchant(null);
    setEditForm({});
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingMerchant) return;
    const timeError = validateTime(String(editForm.deliveryCutoffTime || ''));
    if (timeError) {
      setError(timeError);
      setTimeout(() => setError(null), 3000);
      return;
    }
    setActionLoading(true);
    try {
      const payload = {
        name: editForm.name,
        commissionRate: editForm.commissionRate,
        isActive: editForm.isActive,
        supportsPickup: editForm.supportsPickup,
        pickupLocation: editForm.pickupLocation,
        pickupTimeNote: editForm.pickupTimeNote,
        deliveryDays: editForm.deliveryDays,
        deliveryTime: editForm.deliveryTime,
        deliveryCutoffTime: editForm.deliveryCutoffTime,
        deliveryCutoffDaysBefore: editForm.deliveryCutoffDaysBefore,
        isClosed: editForm.isClosed,
        closedUntil: editForm.closedUntil,
        closedMessage: editForm.closedMessage,
      };
      const res = await apiFetch(`/api/public/admin/merchants/${editingMerchant.code}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setSuccess('商家資料已更新');
        closeEdit();
        loadMerchants();
        if (detailCode === editingMerchant.code) loadDetail(editingMerchant.code);
      } else {
        setError(data.error || '更新失敗');
      }
    } catch (e: any) {
      setError(e.message || '更新失敗');
    } finally {
      setActionLoading(false);
      setTimeout(() => { setSuccess(null); setError(null); }, 3000);
    }
  };

  const handleCreateSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!createForm.code.trim() || !createForm.name.trim() || !createForm.ownerPhone.trim()) {
      setError('請填寫 GS Code、商家名稱及負責人電話');
      return;
    }
    const timeError = validateTime(createForm.deliveryCutoffTime);
    if (timeError) {
      setError(timeError);
      setTimeout(() => setError(null), 3000);
      return;
    }
    setActionLoading(true);
    try {
      const res = await apiFetch('/api/public/admin/merchants', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          code: createForm.code.trim(),
          name: createForm.name.trim(),
          ownerPhone: createForm.ownerPhone.trim(),
          commissionRate: Number(createForm.commissionRate) || 15,
          supportsPickup: createForm.supportsPickup,
          pickupLocation: createForm.pickupLocation.trim() || undefined,
          pickupTimeNote: createForm.pickupTimeNote.trim() || undefined,
          deliveryDays: createForm.deliveryDays,
          deliveryTime: createForm.deliveryTime.trim() || undefined,
          deliveryCutoffTime: createForm.deliveryCutoffTime.trim() || undefined,
          deliveryCutoffDaysBefore: Number(createForm.deliveryCutoffDaysBefore) || 1,
          isClosed: createForm.isClosed,
          closedUntil: createForm.closedUntil.trim() || undefined,
          closedMessage: createForm.closedMessage.trim() || undefined,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setSuccess('商家已新增');
        setShowCreateModal(false);
        setCreateForm({
          code: '',
          name: '',
          ownerPhone: '',
          commissionRate: 15,
          supportsPickup: false,
          pickupLocation: '',
          pickupTimeNote: '',
          deliveryDays: [2, 4, 6],
          deliveryTime: '16:00-18:00',
          deliveryCutoffTime: '20:00',
          deliveryCutoffDaysBefore: 1,
          isClosed: false,
          closedUntil: '',
          closedMessage: '',
        });
        loadMerchants();
      } else {
        setError(data.error || '新增失敗');
      }
    } catch (e: any) {
      setError(e.message || '新增失敗');
    } finally {
      setActionLoading(false);
      setTimeout(() => { setSuccess(null); setError(null); }, 3000);
    }
  };

  const downloadReport = async (merchant: Merchant) => {
    const month = getCurrentMonth();
    setActionLoading(true);
    try {
      const res = await apiFetch(`/api/public/admin/merchants/${merchant.code}/report?month=${month}`);
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || '獲取報表失敗');
      }
      const report = (await res.json()) as ReportData;

      const wsData = [
        ['好餸社企 — 鄰里商家結算報告'],
        [],
        ['結算月份', report.month],
        ['商家名稱', report.merchant.name],
        ['GS Code', report.merchant.code],
        ['負責人電話', report.merchant.ownerPhone],
        ['佣金率', `${report.merchant.commissionRate}%`],
        [],
        ['訂單明細'],
        ['訂單編號', '落單日期', '客戶名稱', '客戶電話', '地址', '訂單金額'],
        ...report.orders.map((o) => [
          o.orderNum,
          formatDate(o.createdAt),
          o.name,
          o.phone,
          o.address,
          Math.round(o.totalPrice / 100),
        ]),
        [],
        ['佣金明細'],
        ['佣金 ID', '訂單 ID', '佣金率', '佣金金額', '狀態', '建立日期'],
        ...report.commissions.map((c) => [
          c.id,
          c.orderId,
          `${c.commissionRate}%`,
          Math.round(c.amount / 100),
          c.status === 'paid' ? '已支付' : '待結算',
          formatDate(c.createdAt),
        ]),
        [],
        ['總營業額', Math.round(report.summary.revenue / 100)],
        ['待結佣金', Math.round(report.summary.pendingCommission / 100)],
        ['已結佣金', Math.round(report.summary.paidCommission / 100)],
      ];

      const ws = XLSX.utils.aoa_to_sheet(wsData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, '結算報告');
      XLSX.writeFile(wb, `merchant-${report.merchant.code}-${report.month}.xlsx`);
    } catch (e: any) {
      setError(e.message || '下載報表失敗');
      setTimeout(() => setError(null), 3000);
    } finally {
      setActionLoading(false);
    }
  };

  const copyDashboardUrl = (url: string) => {
    navigator.clipboard.writeText(url).catch(() => {});
    setCopiedDashboardUrl(true);
    setTimeout(() => setCopiedDashboardUrl(false), 2000);
  };

  const handleLogout = () => {
    if (confirm('確定要登出嗎？')) {
      localStorage.removeItem('admin_token');
      localStorage.removeItem('admin_user');
      navigate('/admin');
    }
  };

  return (
    <AdminLayout currentPage="merchants" onLogout={handleLogout}>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
          <Store className="w-6 h-6 text-orange-600" />
          鄰里商家管理
        </h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">管理鄰里商家、自選屋苑與佣金結算</p>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 flex justify-between items-center dark:bg-red-900/20 dark:border-red-800 dark:text-red-300">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="text-red-700 hover:text-red-900 dark:text-red-300 dark:hover:text-red-100"><X className="w-5 h-5" /></button>
        </div>
      )}
      {success && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700 flex justify-between items-center dark:bg-green-900/20 dark:border-green-800 dark:text-green-300">
          <span className="flex items-center gap-1"><CheckCircle className="w-4 h-4" /> {success}</span>
          <button onClick={() => setSuccess(null)} className="text-green-700 hover:text-green-900 dark:text-green-300 dark:hover:text-green-100"><X className="w-5 h-5" /></button>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow border border-gray-200 dark:border-gray-700">
          <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">本月總營業額</div>
          <div className="text-2xl font-bold text-gray-800 dark:text-white">{formatCents(summary.totalMonthRevenue)}</div>
        </div>
        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow border border-gray-200 dark:border-gray-700">
          <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">待結佣金總額</div>
          <div className="text-2xl font-bold text-gray-800 dark:text-white">{formatCents(summary.totalMonthPendingCommission)}</div>
        </div>
        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow border border-gray-200 dark:border-gray-700">
          <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">已結佣金總額</div>
          <div className="text-2xl font-bold text-gray-800 dark:text-white">{formatCents(summary.totalMonthPaidCommission)}</div>
        </div>
      </div>

      {/* Filters + Actions */}
      <div className="mb-6 bg-white dark:bg-gray-800 p-4 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="flex flex-col lg:flex-row lg:items-end gap-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 flex-1">
            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">搜尋</label>
              <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="商家名稱 / GS Code / 電話"
                  className="w-full pl-9 pr-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:ring-2 focus:ring-orange-500 outline-none text-sm bg-white dark:bg-gray-800 dark:text-white"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">狀態</label>
              <select
                className="w-full p-2.5 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:ring-2 focus:ring-orange-500 outline-none text-sm bg-white dark:bg-gray-800 dark:text-white"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as any)}
              >
                <option value="all">全部</option>
                <option value="active">啟用中</option>
                <option value="inactive">已停用</option>
              </select>
            </div>
          </div>
          {can(PERMISSIONS.MERCHANTS_MANAGE) && (
            <button
              onClick={() => setShowCreateModal(true)}
              className="inline-flex items-center gap-1 bg-orange-600 text-white px-4 py-2.5 rounded-lg text-sm hover:bg-orange-700 transition-colors"
            >
              <Plus className="w-4 h-4" /> 手動新增商家
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-100 dark:bg-gray-900 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
              <tr>
                <th className="p-4">商家名稱</th>
                <th className="p-4">GS Code</th>
                <th className="p-4">負責人電話</th>
                <th className="p-4">狀態</th>
                <th className="p-4">送貨規則</th>
                <th className="p-4 text-right">本月營業額</th>
                <th className="p-4 text-right">本月佣金</th>
                <th className="p-4">操作</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {loading ? (
                <tr><td colSpan={8} className="p-8 text-center text-gray-400">加載中...</td></tr>
              ) : filteredMerchants.length === 0 ? (
                <tr><td colSpan={8} className="p-8 text-center text-gray-500 dark:text-gray-400">暫無商家記錄</td></tr>
              ) : (
                filteredMerchants.map((m) => (
                  <tr key={m.code} className="hover:bg-orange-50 dark:hover:bg-gray-700 transition-colors">
                    <td className="p-4">
                      <div className="font-medium text-gray-800 dark:text-white">{m.name}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">佣金 {m.commissionRate}%</div>
                    </td>
                    <td className="p-4 font-mono text-gray-600 dark:text-gray-300">{m.code}</td>
                    <td className="p-4 font-mono text-gray-600 dark:text-gray-300">
                      {m.ownerPhone}
                    </td>
                    <td className="p-4">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${toBool(m.isActive) ? STATUS_BADGES.active : STATUS_BADGES.inactive}`}>
                        {toBool(m.isActive) ? '啟用中' : '已停用'}
                      </span>
                      {toBool(m.isClosed) && (
                        <span className="ml-1 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-300">
                          休息中
                        </span>
                      )}
                    </td>
                    <td className="p-4 text-sm text-gray-700 dark:text-gray-300">
                      <div>{formatDeliveryDays(m.deliveryDays)}</div>
                      {m.deliveryTime && <div className="text-xs text-gray-500 dark:text-gray-400">{m.deliveryTime}</div>}
                    </td>
                    <td className="p-4 text-right font-medium text-gray-800 dark:text-white">{formatCents(m.monthRevenue)}</td>
                    <td className="p-4 text-right">
                      <div className="font-medium text-gray-800 dark:text-white">{formatCents((m.monthPendingCommission || 0) + (m.monthPaidCommission || 0))}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        待結 {formatCents(m.monthPendingCommission)} / 已結 {formatCents(m.monthPaidCommission)}
                      </div>
                    </td>
                    <td className="p-4 space-x-2 whitespace-nowrap">
                      <button
                        onClick={() => openDetail(m.code)}
                        className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 text-xs"
                      >
                        <Eye size={14} /> 詳情
                      </button>
                      {can(PERMISSIONS.MERCHANTS_MANAGE) && (
                        <>
                          <button
                            onClick={() => openEdit(m)}
                            className="inline-flex items-center gap-1 text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 text-xs"
                          >
                            <Edit2 size={14} /> 編輯
                          </button>
                          <button
                            onClick={() => handleToggleActive(m)}
                            disabled={actionLoading}
                            className={`inline-flex items-center gap-1 text-xs ${toBool(m.isActive) ? 'text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300' : 'text-green-600 hover:text-green-700 dark:text-green-400 dark:hover:text-green-300'}`}
                          >
                            {toBool(m.isActive) ? <><PowerOff size={14} /> 停用</> : <><Power size={14} /> 啟用</>}
                          </button>
                          <button
                            onClick={() => downloadReport(m)}
                            disabled={actionLoading}
                            className="inline-flex items-center gap-1 text-orange-600 hover:text-orange-700 dark:text-orange-400 dark:hover:text-orange-300 text-xs"
                          >
                            <Download size={14} /> 報告
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Detail Modal */}
      {detailCode && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                <Store className="w-5 h-5 text-orange-600" />
                商家詳情
              </h2>
              <button onClick={() => setDetailCode(null)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 overflow-y-auto flex-1">
              {loadingDetail || !detail ? (
                <div className="text-center text-gray-400 py-8">加載中...</div>
              ) : (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="bg-gray-50 dark:bg-gray-900 p-4 rounded-lg">
                      <div className="text-xs text-gray-500 dark:text-gray-400">商家名稱</div>
                      <div className="font-semibold text-gray-800 dark:text-white">{detail.merchant.name}</div>
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-900 p-4 rounded-lg">
                      <div className="text-xs text-gray-500 dark:text-gray-400">GS Code</div>
                      <div className="font-mono text-gray-800 dark:text-white">{detail.merchant.code}</div>
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-900 p-4 rounded-lg">
                      <div className="text-xs text-gray-500 dark:text-gray-400">負責人電話</div>
                      <div className="font-mono text-gray-800 dark:text-white">{detail.merchant.ownerPhone}</div>
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-900 p-4 rounded-lg sm:col-span-2">
                      <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">商家專頁連結</div>
                      {detail.dashboardUrl ? (
                        <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                          <a
                            href={detail.dashboardUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1 text-sm text-orange-600 hover:text-orange-700 underline truncate"
                          >
                            <ExternalLink size={14} /> 開啟商家後台
                          </a>
                          <button
                            type="button"
                            onClick={() => copyDashboardUrl(detail.dashboardUrl!)}
                            className="inline-flex items-center gap-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 px-2 py-1 rounded transition-colors"
                          >
                            <Copy size={12} /> {copiedDashboardUrl ? '已複製' : '複製連結'}
                          </button>
                        </div>
                      ) : (
                        <div className="text-sm text-gray-400">尚未建立專頁</div>
                      )}
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-900 p-4 rounded-lg">
                      <div className="text-xs text-gray-500 dark:text-gray-400">佣金率</div>
                      <div className="font-semibold text-gray-800 dark:text-white">{detail.merchant.commissionRate}%</div>
                    </div>
                  </div>

                  {toBool(detail.merchant.supportsPickup) && (
                    <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-100 dark:border-orange-800 p-4 rounded-lg">
                      <div className="flex items-center gap-2 text-orange-800 dark:text-orange-300 font-medium mb-1">
                        <MapPin size={16} /> 自取設定
                      </div>
                      <div className="text-sm text-gray-700 dark:text-gray-300">{detail.merchant.pickupLocation}</div>
                      {detail.merchant.pickupTimeNote && (
                        <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">{detail.merchant.pickupTimeNote}</div>
                      )}
                    </div>
                  )}

                  <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 p-4 rounded-lg">
                    <div className="flex items-center gap-2 text-blue-800 dark:text-blue-300 font-medium mb-1">
                      <Truck size={16} /> 送貨規則
                    </div>
                    <div className="text-sm text-gray-700 dark:text-gray-300">
                      {formatDeliveryDays(detail.merchant.deliveryDays)}
                      {detail.merchant.deliveryTime && ` · ${detail.merchant.deliveryTime}`}
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      截單時間：{detail.merchant.deliveryCutoffTime || '20:00'}（截單前 {detail.merchant.deliveryCutoffDaysBefore || 1} 天）
                    </div>
                    {toBool(detail.merchant.isClosed) && (
                      <div className="mt-2 text-sm text-red-700 dark:text-red-300 font-medium">
                        休息中{detail.merchant.closedUntil ? ` 至 ${detail.merchant.closedUntil}` : ''}
                        {detail.merchant.closedMessage ? ` — ${detail.merchant.closedMessage}` : ''}
                      </div>
                    )}
                  </div>

                  <div>
                    <h3 className="text-sm font-semibold text-gray-800 dark:text-white mb-3 flex items-center gap-2">
                      <MapPin size={16} /> 自選屋苑
                    </h3>
                    {estateSettingsLoading ? (
                      <p className="text-sm text-gray-500 dark:text-gray-400">載入中...</p>
                    ) : estateSettingsError ? (
                      <p className="text-sm text-red-600 dark:text-red-400">{estateSettingsError}</p>
                    ) : (
                      <div className="space-y-3">
                        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-3">
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
                            <div>
                              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">港九新界</label>
                              <select
                                value={estateRegionFilter}
                                onChange={(e) => {
                                  setEstateRegionFilter(e.target.value as 'all' | 'HK' | 'KL' | 'NT');
                                  setEstateDistrictFilter('all');
                                  setEstateAreaFilter('all');
                                }}
                                className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:ring-2 focus:ring-orange-500 outline-none text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                              >
                                <option value="all">全部</option>
                                {REGIONS.map((r) => (
                                  <option key={r.value} value={r.value}>{r.label}</option>
                                ))}
                              </select>
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">行政區域</label>
                              <select
                                value={estateDistrictFilter}
                                onChange={(e) => {
                                  setEstateDistrictFilter(e.target.value);
                                  setEstateAreaFilter('all');
                                }}
                                className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:ring-2 focus:ring-orange-500 outline-none text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                              >
                                {districtOptions.map((d) => (
                                  <option key={d} value={d === '全部' ? 'all' : d}>{d}</option>
                                ))}
                              </select>
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">具體地區</label>
                              <select
                                value={estateAreaFilter}
                                onChange={(e) => setEstateAreaFilter(e.target.value)}
                                className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:ring-2 focus:ring-orange-500 outline-none text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                              >
                                {areaOptions.map((a) => (
                                  <option key={a} value={a === '全部' ? 'all' : a}>{a}</option>
                                ))}
                              </select>
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">屋苑類型</label>
                              <select
                                value={estateTypeFilter}
                                onChange={(e) => setEstateTypeFilter(e.target.value)}
                                className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:ring-2 focus:ring-orange-500 outline-none text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                              >
                                {estateTypeOptions.map((t) => (
                                  <option key={t} value={t === '全部' ? 'all' : t}>{t}</option>
                                ))}
                              </select>
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">搜尋屋苑</label>
                              <div className="relative">
                                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                <input
                                  type="text"
                                  placeholder="輸入屋苑名稱"
                                  value={estateSearch}
                                  onChange={(e) => setEstateSearch(e.target.value)}
                                  className="w-full pl-9 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:ring-2 focus:ring-orange-500 outline-none text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                                />
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3 mb-3 text-sm text-amber-800 dark:text-amber-200">
                          <p className="font-bold">備註：最低消費設定機制</p>
                          <p className="mt-1">
                            系統技術上支援「逐個屋苑」設定不同最低消費，但現階段為方便鄰里店操作，請優先在「店鋪設定」輸入統一最低消費；儲存後系統會自動同步到所有已啟用屋苑。
                            如有特殊需要，仍可在此逐屋苑覆寫。
                          </p>
                        </div>

                        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                          <div className="overflow-x-auto">
                            <table className="min-w-full text-sm">
                              <thead className="bg-gray-100 dark:bg-gray-900 text-left text-xs uppercase tracking-wider text-gray-600 dark:text-gray-400">
                                <tr>
                                  <th className="p-3">名稱</th>
                                  <th className="p-3">行政區域</th>
                                  <th className="p-3">具體地區</th>
                                  <th className="p-3">類型</th>
                                  <th className="p-3">最低消費</th>
                                  <th className="p-3">啟用</th>
                                  <th className="p-3">操作</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                                {paginatedEstateSettings.map((row) => {
                                  const draft = estateDrafts[row.id] || { minOrderAmount: row.minOrderAmount || 0, isActive: row.isActive };
                                  return (
                                    <tr key={row.id}>
                                      <td className="p-3 text-gray-900 dark:text-white font-medium">{row.name}</td>
                                      <td className="p-3 text-gray-700 dark:text-gray-300">{row.district || '-'}</td>
                                      <td className="p-3 text-gray-700 dark:text-gray-300">{row.area || '-'}</td>
                                      <td className="p-3 text-gray-700 dark:text-gray-300">{row.estateType || '-'}</td>
                                      <td className="p-3">
                                        <input
                                          type="number"
                                          min={0}
                                          value={draft.minOrderAmount}
                                          onChange={(e) => updateEstateDraft(row.id, { minOrderAmount: Number(e.target.value) })}
                                          className="w-28 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-orange-500"
                                        />
                                      </td>
                                      <td className="p-3">
                                        <input
                                          type="checkbox"
                                          checked={draft.isActive}
                                          onChange={(e) => updateEstateDraft(row.id, { isActive: e.target.checked })}
                                          className="w-4 h-4 text-orange-600 rounded focus:ring-orange-500"
                                        />
                                      </td>
                                      <td className="p-3">
                                        {row.settingId ? (
                                          <div className="inline-flex items-center gap-2">
                                            <button
                                              onClick={() => handleSaveEstateSetting(row)}
                                              disabled={actionLoading}
                                              className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded-lg transition disabled:opacity-50"
                                            >
                                              <Save size={12} /> 儲存
                                            </button>
                                            <button
                                              onClick={() => handleRemoveEstateSetting(row)}
                                              disabled={actionLoading}
                                              className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/40 text-xs font-medium rounded-lg transition disabled:opacity-50"
                                            >
                                              <Trash2 size={12} /> 移除
                                            </button>
                                          </div>
                                        ) : (
                                          <button
                                            onClick={() => handleJoinEstate(row)}
                                            disabled={actionLoading}
                                            className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-green-600 hover:bg-green-700 text-white text-xs font-medium rounded-lg transition disabled:opacity-50"
                                          >
                                            <Plus size={12} /> 加入服務
                                          </button>
                                        )}
                                      </td>
                                    </tr>
                                  );
                                })}
                                {paginatedEstateSettings.length === 0 && (
                                  <tr>
                                    <td colSpan={6} className="p-4 text-center text-gray-500 dark:text-gray-400">
                                      沒有符合條件的屋苑
                                    </td>
                                  </tr>
                                )}
                              </tbody>
                            </table>
                          </div>
                          {filteredEstateSettings.length > ESTATES_PER_PAGE && (
                            <div className="p-3 border-t border-gray-200 dark:border-gray-700 flex justify-center items-center gap-3">
                              <button
                                disabled={estatePage <= 1}
                                onClick={() => setEstatePage((p) => p - 1)}
                                className="px-3 py-1.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded text-sm hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50"
                              >
                                上一頁
                              </button>
                              <span className="text-sm text-gray-600 dark:text-gray-400">
                                第 {estatePage} / {estateTotalPages} 頁
                              </span>
                              <button
                                disabled={estatePage >= estateTotalPages}
                                onClick={() => setEstatePage((p) => p + 1)}
                                className="px-3 py-1.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded text-sm hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50"
                              >
                                下一頁
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  <div>
                    <h3 className="text-sm font-semibold text-gray-800 dark:text-white mb-3 flex items-center gap-2">
                      <ShoppingBag size={16} /> 最近訂單
                    </h3>
                    {detail.recentOrders.length === 0 ? (
                      <p className="text-sm text-gray-500 dark:text-gray-400">暫無訂單</p>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="min-w-full text-sm">
                          <thead className="bg-gray-100 dark:bg-gray-900 text-left text-xs uppercase tracking-wider text-gray-600 dark:text-gray-400">
                            <tr>
                              <th className="p-3">訂單編號</th>
                              <th className="p-3">日期</th>
                              <th className="p-3">客戶</th>
                              <th className="p-3 text-right">金額</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                            {detail.recentOrders.map((o) => (
                              <tr key={o.id}>
                                <td className="p-3 font-mono text-gray-700 dark:text-gray-300">{o.orderNum}</td>
                                <td className="p-3 text-gray-600 dark:text-gray-400">{formatDate(o.createdAt)}</td>
                                <td className="p-3">
                                  <span className="group relative cursor-help text-gray-700 dark:text-gray-300">
                                    {o.name} / {maskPhone(o.phone)}
                                    <span className="hidden group-hover:block absolute left-0 -top-8 bg-gray-800 text-white text-xs px-2 py-1 rounded whitespace-nowrap z-10">
                                      {o.phone}
                                    </span>
                                  </span>
                                </td>
                                <td className="p-3 text-right font-medium text-gray-800 dark:text-white">{formatCents(o.totalPrice)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>

                  <div>
                    <h3 className="text-sm font-semibold text-gray-800 dark:text-white mb-3 flex items-center gap-2">
                      <DollarSign size={16} /> 佣金記錄
                    </h3>
                    {detail.commissions.length === 0 ? (
                      <p className="text-sm text-gray-500 dark:text-gray-400">暫無佣金記錄</p>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="min-w-full text-sm">
                          <thead className="bg-gray-100 dark:bg-gray-900 text-left text-xs uppercase tracking-wider text-gray-600 dark:text-gray-400">
                            <tr>
                              <th className="p-3">月份</th>
                              <th className="p-3">訂單 ID</th>
                              <th className="p-3 text-right">佣金</th>
                              <th className="p-3">狀態</th>
                              <th className="p-3">操作</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                            {detail.commissions.map((c) => (
                              <tr key={c.id}>
                                <td className="p-3 text-gray-700 dark:text-gray-300">{c.month}</td>
                                <td className="p-3 font-mono text-gray-600 dark:text-gray-400">{c.orderId}</td>
                                <td className="p-3 text-right font-medium text-gray-800 dark:text-white">{formatCents(c.amount)}</td>
                                <td className="p-3">
                                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${c.status === 'paid' ? STATUS_BADGES.paid : STATUS_BADGES.pending}`}>
                                    {c.status === 'paid' ? '已支付' : '待結算'}
                                  </span>
                                </td>
                                <td className="p-3">
                                  {can(PERMISSIONS.MERCHANTS_MANAGE) && c.status === 'pending' && (
                                    <button
                                      onClick={() => handleMarkCommissionPaid(c)}
                                      disabled={actionLoading}
                                      className="inline-flex items-center gap-1 text-green-600 hover:text-green-700 dark:text-green-400 dark:hover:text-green-300 text-xs"
                                    >
                                      <CheckCircle size={12} /> 標記已支付
                                    </button>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>

                  <div>
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
                      <h3 className="text-sm font-semibold text-gray-800 dark:text-white flex items-center gap-2">
                        <BarChart3 size={16} /> 推薦流量報表
                      </h3>
                      <div className="flex items-center gap-2">
                        <input
                          type="month"
                          value={referralReportMonth}
                          onChange={(e) => setReferralReportMonth(e.target.value)}
                          className="px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-orange-500"
                        />
                        <button
                          onClick={() => loadReferralReport(referralReportMonth)}
                          disabled={referralReportLoading}
                          className="px-3 py-1.5 bg-orange-600 hover:bg-orange-700 text-white text-xs font-medium rounded-lg transition disabled:opacity-50"
                        >
                          {referralReportLoading ? <Loader2 size={12} className="animate-spin" /> : '查詢'}
                        </button>
                      </div>
                    </div>
                    {referralReportLoading ? (
                      <p className="text-sm text-gray-500 dark:text-gray-400">載入中...</p>
                    ) : referralReportError ? (
                      <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg text-yellow-700 dark:text-yellow-300 text-sm">
                        {referralReportError}
                      </div>
                    ) : !referralReport ? (
                      <p className="text-sm text-gray-500 dark:text-gray-400">選擇月份並按查詢以查看報表</p>
                    ) : (
                      <div className="space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                          <div className="bg-white dark:bg-gray-800 p-3 rounded-lg border border-gray-200 dark:border-gray-700">
                            <div className="text-xs text-gray-500 dark:text-gray-400">總點擊</div>
                            <div className="text-xl font-bold text-gray-800 dark:text-white">{referralReport.clicks.toLocaleString('zh-HK')}</div>
                          </div>
                          <div className="bg-white dark:bg-gray-800 p-3 rounded-lg border border-gray-200 dark:border-gray-700">
                            <div className="text-xs text-gray-500 dark:text-gray-400">總轉化</div>
                            <div className="text-xl font-bold text-gray-800 dark:text-white">{referralReport.conversions.toLocaleString('zh-HK')}</div>
                          </div>
                        </div>
                        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                          <div className="overflow-x-auto">
                            <table className="min-w-full text-sm">
                              <thead className="bg-gray-100 dark:bg-gray-900 text-left text-xs uppercase tracking-wider text-gray-600 dark:text-gray-400">
                                <tr>
                                  <th className="p-3">商家</th>
                                  <th className="p-3">GS Code</th>
                                  <th className="p-3 text-right">點擊</th>
                                  <th className="p-3 text-right">訂單</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                                {referralReport.byMerchant.map((row) => (
                                  <tr key={row.merchantCode}>
                                    <td className="p-3 text-gray-900 dark:text-white font-medium">{row.merchantName}</td>
                                    <td className="p-3 font-mono text-gray-600 dark:text-gray-400">{row.merchantCode}</td>
                                    <td className="p-3 text-right text-gray-700 dark:text-gray-300">{row.clicks.toLocaleString('zh-HK')}</td>
                                    <td className="p-3 text-right text-gray-700 dark:text-gray-300">{row.orders.toLocaleString('zh-HK')}</td>
                                  </tr>
                                ))}
                                {referralReport.byMerchant.length === 0 && (
                                  <tr>
                                    <td colSpan={4} className="p-4 text-center text-gray-500 dark:text-gray-400">
                                      該月份沒有推薦流量資料
                                    </td>
                                  </tr>
                                )}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
            <div className="p-4 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => setDetailCode(null)}
                className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-white rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600"
              >
                關閉
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editingMerchant && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto p-6">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <Edit2 className="w-5 h-5 text-orange-600" />
              編輯商家資料
            </h2>
            <form onSubmit={handleEditSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">店 / 負責人顯示名稱</label>
                <input
                  type="text"
                  required
                  value={editForm.name || ''}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  placeholder=" storefront hero 顯示的名稱"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500 outline-none bg-white dark:bg-gray-800 dark:text-white"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">此名稱會顯示在 storefront hero 上。</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">佣金率 (%)</label>
                <input
                  type="number"
                  min={0}
                  max={100}
                  required
                  value={editForm.commissionRate || 0}
                  onChange={(e) => setEditForm({ ...editForm, commissionRate: Number(e.target.value) })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500 outline-none bg-white dark:bg-gray-800 dark:text-white"
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  id="edit-active"
                  type="checkbox"
                  checked={!!editForm.isActive}
                  onChange={(e) => setEditForm({ ...editForm, isActive: e.target.checked })}
                  className="w-4 h-4 text-orange-600 rounded"
                />
                <label htmlFor="edit-active" className="text-sm text-gray-700 dark:text-gray-300">啟用商家</label>
              </div>
              <div className="flex items-center gap-2">
                <input
                  id="edit-pickup"
                  type="checkbox"
                  checked={!!editForm.supportsPickup}
                  onChange={(e) => setEditForm({ ...editForm, supportsPickup: e.target.checked })}
                  className="w-4 h-4 text-orange-600 rounded"
                />
                <label htmlFor="edit-pickup" className="text-sm text-gray-700 dark:text-gray-300">支援自取</label>
              </div>
              {editForm.supportsPickup && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">自取地點</label>
                    <input
                      type="text"
                      value={editForm.pickupLocation || ''}
                      onChange={(e) => setEditForm({ ...editForm, pickupLocation: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500 outline-none bg-white dark:bg-gray-800 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">自取時間備註</label>
                    <input
                      type="text"
                      value={editForm.pickupTimeNote || ''}
                      onChange={(e) => setEditForm({ ...editForm, pickupTimeNote: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500 outline-none bg-white dark:bg-gray-800 dark:text-white"
                    />
                  </div>
                </>
              )}

              <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                <h3 className="text-sm font-bold text-gray-800 dark:text-white mb-3 flex items-center gap-2">
                  <Truck size={16} /> 送貨規則
                </h3>
                <div className="mb-3">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">每週送貨日</label>
                  <div className="flex flex-wrap gap-3">
                    {['日', '一', '二', '三', '四', '五', '六'].map((label, idx) => (
                      <label key={idx} className="flex items-center gap-1.5 text-sm text-gray-700 dark:text-gray-300">
                        <input
                          type="checkbox"
                          checked={(editForm.deliveryDays || []).includes(idx)}
                          onChange={(e) => {
                            const current = new Set(editForm.deliveryDays || []);
                            if (e.target.checked) current.add(idx); else current.delete(idx);
                            setEditForm({ ...editForm, deliveryDays: Array.from(current).sort((a, b) => a - b) });
                          }}
                          className="w-4 h-4 text-orange-600 rounded"
                        />
                        {label}
                      </label>
                    ))}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 mb-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">送貨時段</label>
                    <input
                      type="text"
                      value={editForm.deliveryTime || ''}
                      onChange={(e) => setEditForm({ ...editForm, deliveryTime: e.target.value })}
                      placeholder="例如：16:00-18:00"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500 outline-none bg-white dark:bg-gray-800 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">截單時間</label>
                    <input
                      type="text"
                      value={editForm.deliveryCutoffTime || ''}
                      onChange={(e) => setEditForm({ ...editForm, deliveryCutoffTime: e.target.value })}
                      placeholder="例如：20:00"
                      pattern="^([0-1]?\d|2[0-3]):([0-5]\d)$"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500 outline-none bg-white dark:bg-gray-800 dark:text-white"
                    />
                    {validateTime(String(editForm.deliveryCutoffTime || '')) && (
                      <p className="text-xs text-red-600 mt-1">{validateTime(String(editForm.deliveryCutoffTime || ''))}</p>
                    )}
                  </div>
                </div>
                <div className="mb-3">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">截單前幾天</label>
                  <input
                    type="number"
                    min={0}
                    max={7}
                    value={editForm.deliveryCutoffDaysBefore || 1}
                    onChange={(e) => setEditForm({ ...editForm, deliveryCutoffDaysBefore: Number(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500 outline-none bg-white dark:bg-gray-800 dark:text-white"
                  />
                </div>
                <div className="flex items-center gap-2 mb-3">
                  <input
                    id="edit-closed"
                    type="checkbox"
                    checked={!!editForm.isClosed}
                    onChange={(e) => setEditForm({ ...editForm, isClosed: e.target.checked })}
                    className="w-4 h-4 text-orange-600 rounded"
                  />
                  <label htmlFor="edit-closed" className="text-sm text-gray-700 dark:text-gray-300">關店休息</label>
                </div>
                {editForm.isClosed && (
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">休息至</label>
                      <input
                        type="date"
                        value={editForm.closedUntil || ''}
                        onChange={(e) => setEditForm({ ...editForm, closedUntil: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500 outline-none bg-white dark:bg-gray-800 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">關店訊息</label>
                      <input
                        type="text"
                        value={editForm.closedMessage || ''}
                        onChange={(e) => setEditForm({ ...editForm, closedMessage: e.target.value })}
                        placeholder="例如：店外遊，暫停接單"
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500 outline-none bg-white dark:bg-gray-800 dark:text-white"
                      />
                    </div>
                  </div>
                )}
              </div>

              <div className="flex gap-2 pt-2">
                <button
                  type="button"
                  onClick={closeEdit}
                  className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-white rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600"
                >
                  取消
                </button>
                <button
                  type="submit"
                  disabled={actionLoading}
                  className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 flex items-center justify-center gap-1"
                >
                  {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : '儲存變更'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto p-6">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <Plus className="w-5 h-5 text-orange-600" />
              手動新增商家
            </h2>
            <form onSubmit={handleCreateSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">GS Code <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  required
                  value={createForm.code}
                  onChange={(e) => setCreateForm({ ...createForm, code: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '') })}
                  placeholder="YZDCF"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500 outline-none bg-white dark:bg-gray-800 dark:text-white font-mono"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">商家名稱 <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  required
                  value={createForm.name}
                  onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500 outline-none bg-white dark:bg-gray-800 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">負責人電話 <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  required
                  value={createForm.ownerPhone}
                  onChange={(e) => setCreateForm({ ...createForm, ownerPhone: e.target.value })}
                  placeholder="85291234567"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500 outline-none bg-white dark:bg-gray-800 dark:text-white font-mono"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">佣金率 (%)</label>
                <input
                  type="number"
                  min={0}
                  max={100}
                  value={createForm.commissionRate}
                  onChange={(e) => setCreateForm({ ...createForm, commissionRate: Number(e.target.value) })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500 outline-none bg-white dark:bg-gray-800 dark:text-white"
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  id="create-pickup"
                  type="checkbox"
                  checked={createForm.supportsPickup}
                  onChange={(e) => setCreateForm({ ...createForm, supportsPickup: e.target.checked })}
                  className="w-4 h-4 text-orange-600 rounded"
                />
                <label htmlFor="create-pickup" className="text-sm text-gray-700 dark:text-gray-300">支援自取</label>
              </div>
              {createForm.supportsPickup && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">自取地點</label>
                    <input
                      type="text"
                      value={createForm.pickupLocation}
                      onChange={(e) => setCreateForm({ ...createForm, pickupLocation: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500 outline-none bg-white dark:bg-gray-800 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">自取時間備註</label>
                    <input
                      type="text"
                      value={createForm.pickupTimeNote}
                      onChange={(e) => setCreateForm({ ...createForm, pickupTimeNote: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500 outline-none bg-white dark:bg-gray-800 dark:text-white"
                    />
                  </div>
                </>
              )}

              <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                <h3 className="text-sm font-bold text-gray-800 dark:text-white mb-3 flex items-center gap-2">
                  <Truck size={16} /> 送貨規則
                </h3>
                <div className="mb-3">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">每週送貨日</label>
                  <div className="flex flex-wrap gap-3">
                    {['日', '一', '二', '三', '四', '五', '六'].map((label, idx) => (
                      <label key={idx} className="flex items-center gap-1.5 text-sm text-gray-700 dark:text-gray-300">
                        <input
                          type="checkbox"
                          checked={createForm.deliveryDays.includes(idx)}
                          onChange={(e) => {
                            const current = new Set(createForm.deliveryDays);
                            if (e.target.checked) current.add(idx); else current.delete(idx);
                            setCreateForm({ ...createForm, deliveryDays: Array.from(current).sort((a, b) => a - b) });
                          }}
                          className="w-4 h-4 text-orange-600 rounded"
                        />
                        {label}
                      </label>
                    ))}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 mb-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">送貨時段</label>
                    <input
                      type="text"
                      value={createForm.deliveryTime}
                      onChange={(e) => setCreateForm({ ...createForm, deliveryTime: e.target.value })}
                      placeholder="例如：16:00-18:00"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500 outline-none bg-white dark:bg-gray-800 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">截單時間</label>
                    <input
                      type="text"
                      value={createForm.deliveryCutoffTime}
                      onChange={(e) => setCreateForm({ ...createForm, deliveryCutoffTime: e.target.value })}
                      placeholder="例如：20:00"
                      pattern="^([0-1]?\d|2[0-3]):([0-5]\d)$"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500 outline-none bg-white dark:bg-gray-800 dark:text-white"
                    />
                    {validateTime(createForm.deliveryCutoffTime) && (
                      <p className="text-xs text-red-600 mt-1">{validateTime(createForm.deliveryCutoffTime)}</p>
                    )}
                  </div>
                </div>
                <div className="mb-3">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">截單前幾天</label>
                  <input
                    type="number"
                    min={0}
                    max={7}
                    value={createForm.deliveryCutoffDaysBefore}
                    onChange={(e) => setCreateForm({ ...createForm, deliveryCutoffDaysBefore: Number(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500 outline-none bg-white dark:bg-gray-800 dark:text-white"
                  />
                </div>
                <div className="flex items-center gap-2 mb-3">
                  <input
                    id="create-closed"
                    type="checkbox"
                    checked={createForm.isClosed}
                    onChange={(e) => setCreateForm({ ...createForm, isClosed: e.target.checked })}
                    className="w-4 h-4 text-orange-600 rounded"
                  />
                  <label htmlFor="create-closed" className="text-sm text-gray-700 dark:text-gray-300">關店休息</label>
                </div>
                {createForm.isClosed && (
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">休息至</label>
                      <input
                        type="date"
                        value={createForm.closedUntil}
                        onChange={(e) => setCreateForm({ ...createForm, closedUntil: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500 outline-none bg-white dark:bg-gray-800 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">關店訊息</label>
                      <input
                        type="text"
                        value={createForm.closedMessage}
                        onChange={(e) => setCreateForm({ ...createForm, closedMessage: e.target.value })}
                        placeholder="例如：店外遊，暫停接單"
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500 outline-none bg-white dark:bg-gray-800 dark:text-white"
                      />
                    </div>
                  </div>
                )}
              </div>

              <div className="flex gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-white rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600"
                >
                  取消
                </button>
                <button
                  type="submit"
                  disabled={actionLoading}
                  className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 flex items-center justify-center gap-1"
                >
                  {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : '新增商家'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminMerchants;
